import { inject, Injectable } from '@angular/core';
import { defaultIfEmpty, lastValueFrom, filter, take } from 'rxjs';
import { CabinaStore } from '../_signal-store/_cabina/cabina.store';
import { CittaStore } from '../_signal-store/_citta/citta.store';
import { FeedbackStore } from '../_signal-store/_feedback/feedback.store';
import { FormaGiuridicaStore } from '../_signal-store/_forma_giuridica/forma_giuridica.store';
import { FormaGiuridicaCerStore } from '../_signal-store/_forma_giuridica_cer/forma_giuridica_cer.store';
import { ProvinciaStore } from '../_signal-store/_provincia/provincia.store';
import { TipoContattoStore } from '../_signal-store/_tipo_contatto/tipo_contatto.store';
import { TipoPromotoreStore } from '../_signal-store/_tipo_promotore/tipo_promotore.store';
import { TipologiaAssetStore } from '../_signal-store/_tipologia_asset/tipologia_asset.store';
import { TranslateService } from '@ngx-translate/core';
import { IncentivoImpiantoStore } from '../_signal-store/_incentivo_impianto/incentivo_impianto.store';
import { TipoSpazioDisponibileStore } from '../_signal-store/_tipo_spazio_disponibile/tipo_spazio_disponibile.store';
import { TitoloAutorizzativoStore } from '../_signal-store/_titolo_autorizzativo/titolo_autorizzativo.store';
import { RegioneStore } from '../_signal-store/_regione/regione.store';
import { NazioneStore } from '../_signal-store/_nazione/nazione.store';
import { AuthService } from './auth.service';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ReferralService } from './referral.service';
import { CodiceAtecoStore } from '../_signal-store/_codice_ateco/codice_ateco.store';
import { TipologiaRappresentanteStore } from '../_signal-store/_tipologia_rappresentante/tipologia_rappresentante.store';

@Injectable({ providedIn: 'root' })
export class InitService {
  private _tipologiaAsset = inject(TipologiaAssetStore);
  private _nazione = inject(NazioneStore);
  private _citta = inject(CittaStore);
  private _provincia = inject(ProvinciaStore);
  private _regione = inject(RegioneStore);
  private _tipoContatto = inject(TipoContattoStore);
  private _tipoPromotore = inject(TipoPromotoreStore);
  private _formaGiuridica = inject(FormaGiuridicaStore);
  private _formaGiuridicaCer = inject(FormaGiuridicaCerStore);
  private _cabina = inject(CabinaStore);
  private _feedback = inject(FeedbackStore);
  private _translate = inject(TranslateService);
  private _incentivoImpianto = inject(IncentivoImpiantoStore);
  private _tipoSpazioDisponibile = inject(TipoSpazioDisponibileStore);
  private _titoloAutorizzativo = inject(TitoloAutorizzativoStore);
  private _codiceAteco = inject(CodiceAtecoStore);
  private _tipologiaRappresentante = inject(TipologiaRappresentanteStore);
  private _auth = inject(AuthService);
  private _router = inject(Router);

  private _initialized = false;
  private _initSub?: Subscription;

  /**
   * Chiama questa funzione una sola volta (es. in AppComponent).
   * Non blocca: prepara la subscribe e lancia l’inizializzazione al primo NavigationEnd utile.
   */
  async init(): Promise<void> {
    const router = inject(Router);
    const referral = inject(ReferralService);

    if (this._initialized || this._initSub) return;

    // Se la prima navigazione è già avvenuta e non sei su /login, avvia subito
    if (this._router.navigated && !this._router.url.startsWith('/login')) {
      this.runInitOnce().catch(console.error);
      return;
    }

    // Altrimenti attendi il primo NavigationEnd NON /login e poi avvia
    this._initSub = this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        filter((e) => !e.urlAfterRedirects.startsWith('/login')),
        take(1), // si auto-disinscrive al primo evento valido
      )
      .subscribe(() => {
        this.runInitOnce().catch(console.error);
      });

    router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((event) => {
        const tree = router.parseUrl(event.url);
        const qp = tree.queryParams;

        if (qp['amico']) {
          referral.amico = qp['amico'];
          return;
        }

        if (referral.amico) {
          const updated = router.parseUrl(event.url);

          updated.queryParams = {
            ...updated.queryParams,
            amico: referral.amico,
          };

          router.navigateByUrl(updated, { replaceUrl: true });
        }
      });
  }

  private async runInitOnce(): Promise<void> {
    if (this._initialized) return;

    // Non serve più tenere la subscribe viva
    this._initSub?.unsubscribe();
    this._initSub = undefined;

    console.log('🚀 Avvio inizializzazione store...');

    // Prova refresh sessione (non blocca l’init)
    this._auth.refresh().subscribe(() => {});

    // Fase 1: operazioni "bloccanti" prima del resto dell'app
    await Promise.all([
      lastValueFrom(this._translate.use('it').pipe(defaultIfEmpty(null))),
      lastValueFrom(this._tipologiaAsset.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._citta.init().pipe(defaultIfEmpty(null))),
    ]);

    console.log('✅ Inizializzazione asincrona base completata.');

    // Fase 2: il resto in background (senza await)
    Promise.all([
      lastValueFrom(this._nazione.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._provincia.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._regione.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._tipoContatto.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._tipoPromotore.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._formaGiuridica.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._formaGiuridicaCer.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._cabina.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._feedback.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._incentivoImpianto.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(this._codiceAteco.init().pipe(defaultIfEmpty(null))),
      lastValueFrom(
        this._tipologiaRappresentante.init().pipe(defaultIfEmpty(null)),
      ),
      lastValueFrom(
        this._tipoSpazioDisponibile.init().pipe(defaultIfEmpty(null)),
      ),
      lastValueFrom(
        this._titoloAutorizzativo.init().pipe(defaultIfEmpty(null)),
      ),
    ]).catch(console.error);

    this._initialized = true;
    console.log('✅ Inizializzazione store completata.');
  }
}
